"use client";

import { createContext, useContext, useCallback, useEffect, useState, type ReactNode } from "react";

import type { PublicBrandDetails } from "@/lib/public-shop-agent-fetch";
import { type BrandDetails, getBrandDetails } from "@/lib/shop-agent-client";

interface BrandDetailsContextValue {
  brandDetails: BrandDetails | null;
  loading: boolean;
  hasError: boolean;
  refetch: () => Promise<void>;
}

const BrandDetailsContext = createContext<BrandDetailsContextValue | null>(null);

/**
 * Type for initial data that can be passed from server components.
 * Public profile data from the server or the same profile fetched in the client.
 */
type InitialBrandData = BrandDetails | PublicBrandDetails | null;

interface BrandDetailsProviderProps {
  brandName: string;
  children: ReactNode;
  /**
   * Optional initial brand details from server-side fetch.
   * When provided, the provider starts with this data (no loading state)
   * without an additional hydration read.
   */
  initialData?: InitialBrandData;
}

/**
 * Provider that fetches brand details once and shares them with all child components.
 * Supports SSR by accepting initialData from server-side fetch.
 */
export function BrandDetailsProvider({
  brandName,
  children,
  initialData,
}: BrandDetailsProviderProps) {
  // Initialize with server-provided data if available
  const [brandDetails, setBrandDetails] = useState<BrandDetails | null>(initialData ?? null);
  // If we have initial data, start with loading=false
  const [loading, setLoading] = useState(!initialData);
  const [hasError, setHasError] = useState(false);
  const fetchBrandDetails = useCallback(
    async (backgroundRefresh: boolean = false) => {
      try {
        if (!backgroundRefresh) {
          setLoading(true);
          setHasError(false);
        }

        const result = await getBrandDetails(brandName);

        if (result) {
          setBrandDetails(result);
          setHasError(false);
          return;
        }

        // Keep server-provided data if a background refresh fails to return data.
        if (!backgroundRefresh) {
          setBrandDetails(null);
        }
      } catch (error) {
        console.error("Error fetching brand details:", error);
        // Preserve existing data on background refresh failures.
        if (!backgroundRefresh) {
          setHasError(true);
          setBrandDetails(null);
        }
      } finally {
        if (!backgroundRefresh) {
          setLoading(false);
        }
      }
    },
    [brandName]
  );

  const refetch = useCallback(async () => {
    await fetchBrandDetails(false);
  }, [fetchBrandDetails]);

  useEffect(() => {
    // Public SSR profiles are complete; no identity-dependent hydration read.
    if (initialData) {
      return;
    }
    void fetchBrandDetails(Boolean(initialData));
  }, [fetchBrandDetails, initialData]);

  return (
    <BrandDetailsContext.Provider
      value={{
        brandDetails,
        loading,
        hasError,
        refetch,
      }}
    >
      {children}
    </BrandDetailsContext.Provider>
  );
}

/**
 * Hook to access brand details from context
 */
export function useBrandDetails() {
  const context = useContext(BrandDetailsContext);
  if (!context) {
    throw new Error("useBrandDetails must be used within a BrandDetailsProvider");
  }
  return context;
}
